import { ServerResponse, type IncomingMessage } from "http"
import { ParsedUrlQuery } from "querystring"
import { NextApiRequest, NextApiResponse } from "next"
import { NextRouter } from "next/router"
import {
  getAuthParam,
  getEmbeddedParam,
  getOldAuthParam,
  needsSSL,
} from "@/proxy-utils"
import { OLD_AUTH_COOKIE } from "@/types/auth"
import { EmbeddedParamNames } from "@/types/embedded"
import { CurrentUser, ExternalAuthProvider } from "@/types/user"
import { RawAxiosRequestHeaders } from "axios"
import { deleteCookie, getCookies } from "cookies-next"

import { getEmbeddedAuthToken } from "@/lib/embedded/token-storage"
import { env } from "@/lib/env"
import { isClientSide } from "@/lib/is-client-side"
import { isEmbedded } from "@/lib/is-embedded"
import { SIGNED_IN_MARKETING_PATH } from "@/lib/marketing-routes"
import { isEuRegion } from "@/lib/region"
import { isRelativeUrl } from "@/lib/url-utils"
import { assertPresent } from "@/lib/utils/assertions"
import { isExternalUrl } from "@/lib/utils/routing"

export function getCallbackUrlParam(router: NextRouter): string | null {
  const params = router.query
  const s = params["callbackUrl"]
  if (typeof s === "string") {
    const candidate = s.trim()
    if (candidate !== "" && isSafeRelativeCallbackUrl(candidate))
      return candidate
  }
  return null
}

const CALLBACK_DECODE_PASSES = 3

// Callback URLs must remain same-origin relative paths after canonicalization.
export function isSafeRelativeCallbackUrl(
  callbackUrl: string | null | undefined
): callbackUrl is string {
  if (!callbackUrl || typeof callbackUrl !== "string") return false
  if (!isRelativeUrl(callbackUrl)) return false
  if (callbackUrl.includes("\\")) return false

  let value = callbackUrl
  for (let i = 0; i < CALLBACK_DECODE_PASSES; i += 1) {
    if (value.startsWith("//")) return false
    if (value.includes("\\")) return false

    try {
      const decoded = decodeURIComponent(value)
      if (decoded === value) break
      value = decoded
    } catch {
      // Keep the original value when decoding is invalid.
      break
    }
  }

  return true
}

export function getCallbackUrl(router: NextRouter): string {
  let path = getCallbackUrlParam(router)
  if (!path) {
    path = router.asPath
    if (path?.startsWith("/auth/")) path = "/"
  }
  return path || "/"
}

// [AuthCleanup] Migration: Used for clearing legacy OLD_AUTH_COOKIE ("mindtrip.auth")
// This function is still needed during the migration period to clean up
// legacy anonymous user cookies. Works with Auth V2.
export function removeServerAuthCookie(
  req: NextApiRequest | IncomingMessage,
  res: NextApiResponse | ServerResponse
) {
  const authDomain = assertPresent(
    env("NEXT_COOKIE_AUTH_DOMAIN"),
    "NEXT_COOKIE_AUTH_DOMAIN is not set"
  )
  deleteCookie(OLD_AUTH_COOKIE, {
    req,
    res,
    path: "/",
    httpOnly: true,
    sameSite: "lax",
    secure: needsSSL(),
    domain: authDomain,
  })
}

// [AuthCleanup] Generic cookie cleanup utility - works with Auth V2
// Used during sign-out to ensure all cookies are cleared
export function removeAllCookies(
  req: NextApiRequest | IncomingMessage,
  res: NextApiResponse | ServerResponse
) {
  const cookies = getCookies({
    req: req,
    res: res,
  })

  for (const cookie of Object.keys(cookies)) {
    deleteCookie(cookie, {
      req: req,
      res: res,
    })
  }
}

export function isMindtrip(user: CurrentUser) {
  return !!user?.is_mindtrip
}

/** The EU main app serves organization administration. */
export const SIGNED_IN_LANDING_PATH = isEuRegion() ? "/dashboard" : "/home"

/** Anonymous app landing: marketing at `/welcome` in main app, chat in embedded. */
export function anonymousLandingPath(embedded: boolean) {
  return embedded ? "/chat" : SIGNED_IN_MARKETING_PATH
}

/** Embedded fallbacks stay in chat; the main app uses its regional landing. */
export function appLandingPath() {
  return isEmbedded() ? "/chat" : SIGNED_IN_LANDING_PATH
}

export function addEmbeddedParams(
  url: string,
  params: URLSearchParams | ParsedUrlQuery
) {
  const [urlPath, urlQuery] = url.split("?")
  const query = new URLSearchParams(urlQuery)

  if (params) {
    EmbeddedParamNames.forEach((name) => {
      const paramValue = getEmbeddedParam(params, name)
      if (paramValue && !isExternalUrl(url.toString())) {
        query.set(name, paramValue)
      }
    })
  }

  return query.size > 0 ? `${urlPath}?${query.toString()}` : urlPath
}

export function removeAuthParam(url: string) {
  const parsedUrl = new URL(url)
  parsedUrl.searchParams.delete(OLD_AUTH_COOKIE)
  parsedUrl.hash = "" // remove embedded params
  return parsedUrl.toString()
}

export function getAuthTokenFromUrl(
  url: string | undefined
): string | undefined {
  if (!url) return

  let parsedUrl: URL
  try {
    parsedUrl = new URL(url, "http://dummy")
  } catch {
    return
  }

  const authParam = getAuthParam(parsedUrl.searchParams)
  if (authParam) {
    return authParam
  }

  const legacyAuthParam = getOldAuthParam(parsedUrl.searchParams)
  if (legacyAuthParam) {
    return JSON.parse(legacyAuthParam).apiServerToken
  }
}

export function addEmbeddedAuthHeader(headers: RawAxiosRequestHeaders) {
  const authToken = isClientSide() && getEmbeddedAuthToken()

  if (authToken) {
    headers["Authorization"] = "Bearer " + authToken
  }
}

/*
 * Google oauth blocks certain app embedded browsers from logging in. This is a list of those browsers.
 */
export function providerBrowserCheck(
  provider: ExternalAuthProvider,
  browser: string | null | undefined
): boolean {
  if (provider === "google") {
    // for browser name list check https://www.npmjs.com/package/ua-parser-js
    return ["Facebook", "Instagram", "LinkedIn", "TikTok"].includes(
      browser || ""
    )
  }

  return false
}

// Auth paths that could cause redirect loops if used as callback URLs
export const AUTH_PATHS = [
  "/auth/sign-in",
  "/auth/refresh",
  "/auth/sign-out",
  "/auth/create-anon",
]

// Check if a URL contains auth paths that could cause redirect loops
export function containsAuthPath(url: string): boolean {
  return AUTH_PATHS.some((path) => url.includes(path))
}

// Check if a callback URL is safe (doesn't contain nested auth paths or multiple callback layers)
export function isSafeCallbackUrl(callbackUrl?: string | null): boolean {
  if (!callbackUrl) return true // No callback is safe

  try {
    // Decode the callbackUrl to check its actual value
    const decoded = decodeURIComponent(callbackUrl)

    // Check if the decoded callbackUrl contains auth paths
    if (containsAuthPath(decoded)) {
      console.log(
        `[AuthUtils] Auth path detected in callbackUrl: ${decoded.substring(0, 200)}`
      )
      return false
    }

    // Also check if there are multiple layers of callbackUrl encoding
    const callbackUrlCount = (decoded.match(/callbackUrl=/g) || []).length
    if (callbackUrlCount > 1) {
      console.log(
        `[AuthUtils] Multiple nested callbackUrls detected: ${callbackUrlCount} layers`
      )
      return false
    }

    return true
  } catch (error) {
    // If we can't decode it, it might be corrupted - treat as unsafe
    console.warn(
      "[AuthUtils] Failed to decode callbackUrl, treating as unsafe",
      error
    )
    return false
  }
}
